import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Grid,
  Text,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  Input,
  Tag,
  Tooltip,
  useColorMode,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Icon,
} from '@chakra-ui/react';
import { RouteComponentProps } from '@reach/router';
import { gql, useMutation } from '@apollo/client';
import { CheckCircle, Warning, WarningCircle, UploadSimple, FileText, ArrowsClockwise, Copy, Eye, FilePdf } from '@phosphor-icons/react';
import Layout from '../../components/Layout';
import { getEnvironment } from '../../environment';
import { useCustomToast } from '../../hooks/useCustomToast';

// ============================================================================
// Types (mirrors backend types)
// ============================================================================

interface PDFInvoiceMetadata {
  invoiceNumber: string;
  invoiceDate: string;
  reportingPeriodStart: string;
  reportingPeriodEnd: string;
}

interface PDFTransactionTotals {
  returnToDriverCount: number;
  returnToDriverAmount: number;
  pickupCount: number;
  pickupAmount: number;
  acceptanceCount: number;
  acceptanceAmount: number;
  totalExclVat: number;
  vatRate: number;
  vatAmount: number;
  totalInclVat: number;
}

interface PDFAccessPointSummary {
  storeName: string;
  accessPointID: string;
  compensationAmount: number;
  tax: number;
  total: number;
}

interface SystemParcelEvents {
  driverDropOffAt: string | null;
  consigneePickupAt: string | null;
  consigneePickupAckAt: string | null;
  shipperDropOffAt: string | null;
  dispossessedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

interface TransactionComparison {
  trackingNumber: string;
  inPdf: boolean;
  inSystem: boolean;
  pdfService?: string;
  pdfDate?: string;
  pdfAmount?: number;
  pdfCode?: string;
  pdfCodeDescription?: string;
  systemStatus?: string;
  systemEvents?: SystemParcelEvents;
}

interface AccessPointReconciliation {
  accessPointID: string;
  storeName: string;
  existsInSystem: boolean;
  pdfTransactionCount: number;
  pdfCompensatedCount: number;
  systemParcelCount: number;
  matchedCount: number;
  pdfOnlyCount: number;
  systemOnlyCount: number;
  pdfCompensation: number;
  transactions: TransactionComparison[];
}

interface ReconciliationTotals {
  totalAccessPoints: number;
  totalPdfTransactions: number;
  totalPdfCompensatedTransactions: number;
  totalSystemParcels: number;
  totalMatched: number;
  totalPdfOnly: number;
  totalSystemOnly: number;
  matchRate: number;
  totalPdfCompensation: number;
}

interface ReconciliationResult {
  metadata: PDFInvoiceMetadata;
  transactionTotals: PDFTransactionTotals;
  summary: PDFAccessPointSummary[];
  accessPoints: AccessPointReconciliation[];
  totals: ReconciliationTotals;
  processedAt: string;
}

// ============================================================================
// Helper components
// ============================================================================

const formatDate = (isoDate: string) => {
  if (!isoDate) return '-';
  const d = new Date(isoDate);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateTime = (isoDate: string | null) => {
  if (!isoDate) return '-';
  const d = new Date(isoDate);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
};

const serviceLabel = (service: string) => {
  switch (service) {
    case 'ABHOL':
      return 'Abholung';
    case 'RUECKG':
      return 'Rückgabe';
    case 'ANNAH':
      return 'Annahme';
    default:
      return service;
  }
};

const StatusBadge = ({ inPdf, inSystem }: { inPdf: boolean; inSystem: boolean }) => {
  if (inPdf && inSystem) {
    return (
      <Badge colorScheme="green" display="flex" alignItems="center" gap="1">
        <Icon as={CheckCircle} weight="fill" /> Match
      </Badge>
    );
  }
  if (inPdf && !inSystem) {
    return (
      <Badge colorScheme="orange" display="flex" alignItems="center" gap="1">
        <Icon as={Warning} weight="fill" /> Nur PDF
      </Badge>
    );
  }
  return (
    <Badge colorScheme="red" display="flex" alignItems="center" gap="1">
      <Icon as={WarningCircle} weight="fill" /> Nur System
    </Badge>
  );
};

// ============================================================================
// Grouping helpers
// ============================================================================

interface TransactionGroup {
  trackingNumber: string;
  entries: TransactionComparison[];
  /** All PDF entries are coded with 0 compensation and no uncoded companion exists */
  isCodeOnly: boolean;
}

/**
 * Groups transactions by tracking number.
 * Detects "code-only" groups: tracking numbers that ONLY have coded entries (amount=0)
 * with no uncoded compensated entry — meaning UPS flagged them but no compensation was paid.
 */
function groupTransactions(transactions: TransactionComparison[]): TransactionGroup[] {
  const map = new Map<string, TransactionComparison[]>();
  // Preserve order: use the first occurrence position
  const order: string[] = [];
  for (const tx of transactions) {
    if (!map.has(tx.trackingNumber)) {
      map.set(tx.trackingNumber, []);
      order.push(tx.trackingNumber);
    }
    map.get(tx.trackingNumber)!.push(tx);
  }

  return order.map((trackingNumber) => {
    const entries = map.get(trackingNumber)!;
    const pdfEntries = entries.filter((e) => e.inPdf);
    const hasUncodedEntry = pdfEntries.some((e) => !e.pdfCode);
    const hasCodedEntry = pdfEntries.some((e) => !!e.pdfCode);
    const hasAnyCompensation = pdfEntries.some((e) => (e.pdfAmount ?? 0) > 0);
    const isCodeOnly = hasCodedEntry && !hasUncodedEntry && !hasAnyCompensation && pdfEntries.length > 0;
    return { trackingNumber, entries, isCodeOnly };
  });
}

// ============================================================================
// GraphQL Mutations
// ============================================================================

const GENERATE_AUSZAHLUNGSBELEG = gql`
  mutation GenerateAuszahlungsbeleg(
    $accessPointID: String!
    $storeName: String!
    $trackingNumbers: [String!]!
    $reportingPeriodStart: String!
    $reportingPeriodEnd: String!
    $isTest: Boolean!
  ) {
    generateAuszahlungsbeleg(
      accessPointID: $accessPointID
      storeName: $storeName
      trackingNumbers: $trackingNumbers
      reportingPeriodStart: $reportingPeriodStart
      reportingPeriodEnd: $reportingPeriodEnd
      isTest: $isTest
    )
  }
`;

// ============================================================================
// Main component
// ============================================================================

interface ReconciliationProps extends RouteComponentProps { }

export const ReconciliationPage: React.FC<ReconciliationProps> = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ReconciliationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filter, setFilter] = useState<'all' | 'matched' | 'pdf-only' | 'system-only' | 'code-only'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { colorMode } = useColorMode();
  const toast = useCustomToast();

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast({ title: 'Fehler', description: 'Bitte eine PDF-Datei auswählen.', status: 'error' });
        return;
      }
      setSelectedFile(file);
      setResult(null);
      setError(null);
    }
  }, [toast]);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;

    setLoading(true);
    setError(null);

    try {
      const { apiUrl } = getEnvironment();
      const formData = new FormData();
      formData.append('pdf', selectedFile);

      const response = await fetch(`${apiUrl}/api/reconciliation/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: 'Unbekannter Fehler' }));
        throw new Error(err.message || err.error || `HTTP ${response.status}`);
      }

      const data: ReconciliationResult = await response.json();
      setResult(data);
      toast({ title: 'Erfolg', description: 'PDF wurde erfolgreich verarbeitet.', status: 'success' });
    } catch (err: any) {
      setError(err.message);
      toast({ title: 'Fehler', description: err.message, status: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selectedFile, toast]);

  const handleReset = useCallback(() => {
    setSelectedFile(null);
    setResult(null);
    setError(null);
    setFilter('all');
    setSearchTerm('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const filteredAPs = result?.accessPoints?.filter((ap) => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        ap.accessPointID.toLowerCase().includes(search) ||
        ap.storeName.toLowerCase().includes(search)
      );
    }
    return true;
  }) || [];

  return (
    <Layout fullWidth>
      <Flex flexDirection={['column', 'row']} justifyContent="space-between" alignItems="flex-start" mb="6">
        <Box>
          <Text as="h2" fontSize="3xl" fontWeight="bold" fontFamily="heading">
            UPS Gutschrift Abgleich
          </Text>
          <Text color="gray.500" fontSize="sm">
            Lade eine UPS Sammelgutschrift-PDF hoch, um die Vergütungen mit den Systemdaten abzugleichen.
          </Text>
        </Box>
      </Flex>

      {/* Upload Section */}
      <Box
        border="2px dashed"
        borderColor={selectedFile ? 'green.300' : 'gray.300'}
        borderRadius="lg"
        p="6"
        mb="6"
        textAlign="center"
        bg={colorMode === 'light' ? 'gray.50' : 'gray.700'}
        cursor="pointer"
        onClick={() => !loading && fileInputRef.current?.click()}
        _hover={{ borderColor: 'brand.500' }}
        transition="all 0.2s">
        <Input
          type="file"
          accept=".pdf"
          ref={fileInputRef}
          onChange={handleFileSelect}
          display="none"
        />
        {selectedFile ? (
          <Flex direction="column" alignItems="center" gap="2">
            <Icon as={FileText} boxSize="8" color="green.500" />
            <Text fontWeight="semibold">{selectedFile.name}</Text>
            <Text fontSize="sm" color="gray.500">
              {(selectedFile.size / 1024).toFixed(1)} KB
            </Text>
            <Flex gap="2" mt="2">
              <Button
                colorScheme="brand"
                size="sm"
                leftIcon={<UploadSimple />}
                onClick={(e) => { e.stopPropagation(); handleUpload(); }}
                isLoading={loading}
                loadingText="Verarbeite..."
                isDisabled={loading}>
                PDF verarbeiten
              </Button>
              <Button
                variant="outline"
                size="sm"
                leftIcon={<ArrowsClockwise />}
                onClick={(e) => { e.stopPropagation(); handleReset(); }}
                isDisabled={loading}>
                Zurücksetzen
              </Button>
            </Flex>
          </Flex>
        ) : (
          <Flex direction="column" alignItems="center" gap="2">
            <Icon as={UploadSimple} boxSize="8" color="gray.400" />
            <Text fontWeight="semibold">PDF-Datei hier klicken zum Auswählen</Text>
            <Text fontSize="sm" color="gray.500">
              UPS Sammelgutschrift (z.B. Gutschrift 02_2026.pdf)
            </Text>
          </Flex>
        )}
      </Box>

      {/* Loading */}
      {loading && (
        <Flex justifyContent="center" py="12">
          <Spinner size="xl" color="brand.500" thickness="4px" />
        </Flex>
      )}

      {/* Error */}
      {error && (
        <Box bg={colorMode === 'light' ? 'red.50' : 'red.900'} border="1px solid" borderColor={colorMode === 'light' ? 'red.200' : 'red.700'} borderRadius="md" p="4" mb="6">
          <Text color={colorMode === 'light' ? 'red.600' : 'red.200'} fontWeight="semibold">Fehler: {error}</Text>
        </Box>
      )}

      {/* Results */}
      {result && (
        <Box>
          <Tabs variant="enclosed" colorScheme="brand">
            <TabList>
              <Tab>Übersicht</Tab>
              <Tab>AccessPoints ({result.accessPoints.length})</Tab>
              <Tab>Zusammenfassung</Tab>
            </TabList>

            <TabPanels>
              {/* Overview Tab */}
              <TabPanel px="0">
                <OverviewSection result={result} />
              </TabPanel>

              {/* AccessPoints Tab */}
              <TabPanel px="0">
                <Box mb="4">
                  <Flex gap="4" mb="4" flexWrap="wrap" alignItems="center">
                    <Input
                      placeholder="Access Point ID oder Name suchen..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      maxW="300px"
                      size="sm"
                    />
                    <Flex gap="2" flexWrap="wrap">
                      {(['all', 'matched', 'pdf-only', 'system-only', 'code-only'] as const).map((f) => (
                        <Tag
                          key={f}
                          size="md"
                          cursor="pointer"
                          colorScheme={filter === f ? (f === 'code-only' ? 'yellow' : 'brand') : 'gray'}
                          onClick={() => setFilter(f)}>
                          {f === 'all' ? 'Alle' : f === 'matched' ? 'Matches' : f === 'pdf-only' ? 'Nur PDF' : f === 'system-only' ? 'Nur System' : 'Ohne Vergütung'}
                        </Tag>
                      ))}
                    </Flex>
                  </Flex>
                </Box>
                <AccessPointsSection accessPoints={filteredAPs} filter={filter} metadata={result.metadata} />
              </TabPanel>

              {/* Summary Tab */}
              <TabPanel px="0">
                <SummaryTableSection summary={result.summary} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      )}
    </Layout>
  );
};

// ============================================================================
// Overview section
// ============================================================================

const OverviewSection = ({ result }: { result: ReconciliationResult }) => {
  const { metadata, transactionTotals, totals } = result;
  const [detailFilter, setDetailFilter] = useState<'pdf-only' | 'system-only' | 'code-only' | null>(null);
  const toast = useCustomToast();
  const cardBorderColor = useColorModeValue('gray.200', 'gray.600');
  const blueBg = useColorModeValue('blue.50', 'blue.900');
  const blueText = useColorModeValue('blue.700', 'blue.200');
  const blueBorder = useColorModeValue('blue.200', 'blue.700');
  const purpleBg = useColorModeValue('purple.50', 'purple.900');
  const purpleText = useColorModeValue('purple.700', 'purple.200');
  const purpleBorder = useColorModeValue('purple.200', 'purple.700');
  const tealBg = useColorModeValue('teal.50', 'teal.900');
  const tealText = useColorModeValue('teal.700', 'teal.200');
  const tealBorder = useColorModeValue('teal.200', 'teal.700');
  const amountColor = useColorModeValue('gray.600', 'gray.300');
  const detailTableBg = useColorModeValue('gray.50', 'gray.800');

  const handleKpiClick = useCallback((type: 'pdf-only' | 'system-only' | 'code-only') => {
    setDetailFilter((prev) => (prev === type ? null : type));
  }, []);

  // Compute code-only tracking numbers across all APs
  const codeOnlyEntries = useMemo(() => {
    const entries: { accessPointID: string; storeName: string; trackingNumber: string; code: string; systemStatus: string }[] = [];
    for (const ap of result.accessPoints) {
      const groups = groupTransactions(ap.transactions);
      for (const group of groups) {
        if (group.isCodeOnly) {
          // Pick the first coded entry for the code value
          const codedEntry = group.entries.find((e) => e.inPdf && e.pdfCode);
          entries.push({
            accessPointID: ap.accessPointID,
            storeName: ap.storeName,
            trackingNumber: group.trackingNumber,
            code: codedEntry?.pdfCode || '-',
            systemStatus: codedEntry?.systemStatus || group.entries[0]?.systemStatus || '-',
          });
        }
      }
    }
    return entries;
  }, [result.accessPoints]);

  // Collect all entries matching the detail filter across all APs
  const detailEntries = useMemo(() => {
    if (!detailFilter) return [];
    const entries: { accessPointID: string; storeName: string; trackingNumber: string; systemStatus: string }[] = [];
    for (const ap of result.accessPoints) {
      for (const tx of ap.transactions) {
        if (detailFilter === 'pdf-only' && tx.inPdf && !tx.inSystem) {
          entries.push({ accessPointID: ap.accessPointID, storeName: ap.storeName, trackingNumber: tx.trackingNumber, systemStatus: tx.systemStatus || '-' });
        } else if (detailFilter === 'system-only' && !tx.inPdf && tx.inSystem) {
          entries.push({ accessPointID: ap.accessPointID, storeName: ap.storeName, trackingNumber: tx.trackingNumber, systemStatus: tx.systemStatus || '-' });
        }
      }
    }
    return entries;
  }, [detailFilter, result.accessPoints]);

  const handleCopy = useCallback(() => {
    if (detailFilter === 'code-only') {
      if (codeOnlyEntries.length === 0) return;
      const header = 'AccessPoint\tTracking Number\tCode\tSystem Status';
      const rows = codeOnlyEntries.map((e) => `${e.accessPointID}\t${e.trackingNumber}\t${e.code}\t${e.systemStatus}`);
      const text = [header, ...rows].join('\n');
      navigator.clipboard.writeText(text).then(() => {
        toast({ title: 'Kopiert', description: `${codeOnlyEntries.length} Einträge in die Zwischenablage kopiert.`, status: 'success', duration: 2000 });
      });
    } else {
      if (detailEntries.length === 0) return;
      const header = 'AccessPoint\tTracking Number\tSystem Status';
      const rows = detailEntries.map((e) => `${e.accessPointID}\t${e.trackingNumber}\t${e.systemStatus}`);
      const text = [header, ...rows].join('\n');
      navigator.clipboard.writeText(text).then(() => {
        toast({ title: 'Kopiert', description: `${detailEntries.length} Einträge in die Zwischenablage kopiert.`, status: 'success', duration: 2000 });
      });
    }
  }, [detailFilter, detailEntries, codeOnlyEntries, toast]);

  return (
    <Box>
      {/* Metadata */}
      <Grid templateColumns={['1fr', '1fr 1fr', '1fr 1fr 1fr 1fr']} gap="4" mb="6">
        <Box p="4" border="1px solid" borderColor={cardBorderColor} borderRadius="md">
          <Text fontSize="xs" color="gray.500" textTransform="uppercase">
            Rechnungsnr.
          </Text>
          <Text fontWeight="bold">{metadata.invoiceNumber}</Text>
        </Box>
        <Box p="4" border="1px solid" borderColor={cardBorderColor} borderRadius="md">
          <Text fontSize="xs" color="gray.500" textTransform="uppercase">
            Rechnungsdatum
          </Text>
          <Text fontWeight="bold">{formatDate(metadata.invoiceDate)}</Text>
        </Box>
        <Box p="4" border="1px solid" borderColor={cardBorderColor} borderRadius="md">
          <Text fontSize="xs" color="gray.500" textTransform="uppercase">
            Berichtszeitraum
          </Text>
          <Text fontWeight="bold">
            {formatDate(metadata.reportingPeriodStart)} – {formatDate(metadata.reportingPeriodEnd)}
          </Text>
        </Box>
        <Box p="4" border="1px solid" borderColor={cardBorderColor} borderRadius="md">
          <Text fontSize="xs" color="gray.500" textTransform="uppercase">
            Verarbeitet am
          </Text>
          <Text fontWeight="bold">{formatDateTime(result.processedAt)}</Text>
        </Box>
      </Grid>

      {/* KPI Cards */}
      <Grid templateColumns={['1fr 1fr', '1fr 1fr 1fr', 'repeat(6, 1fr)']} gap="4" mb="6">
        <Stat p="4" border="1px solid" borderColor={cardBorderColor} borderRadius="md">
          <StatLabel>Gesamtvergütung</StatLabel>
          <StatNumber fontSize="xl" color="green.500">{formatCurrency(transactionTotals.totalInclVat)}</StatNumber>
          <StatHelpText>{formatCurrency(transactionTotals.totalExclVat)} netto + {transactionTotals.vatRate}% MwSt</StatHelpText>
        </Stat>
        <Stat p="4" border="1px solid" borderColor={cardBorderColor} borderRadius="md">
          <StatLabel>Access Points</StatLabel>
          <StatNumber fontSize="xl">{totals.totalAccessPoints}</StatNumber>
          <StatHelpText>mit Transaktionen</StatHelpText>
        </Stat>
        <Stat p="4" border="1px solid" borderColor={cardBorderColor} borderRadius="md">
          <StatLabel>Match-Rate</StatLabel>
          <StatNumber fontSize="xl" color={totals.matchRate >= 90 ? 'green.500' : totals.matchRate >= 70 ? 'orange.500' : 'red.500'}>
            {totals.matchRate}%
          </StatNumber>
          <StatHelpText>{totals.totalMatched} von {totals.totalPdfTransactions}</StatHelpText>
        </Stat>
        <Stat
          p="4"
          border={detailFilter === 'pdf-only' ? '2px solid' : '1px solid'}
          borderColor={detailFilter === 'pdf-only' ? 'orange.400' : cardBorderColor}
          borderRadius="md"
          cursor="pointer"
          onClick={() => handleKpiClick('pdf-only')}
          _hover={{ borderColor: 'orange.300' }}
          transition="all 0.15s"
        >
          <StatLabel>Nur in PDF</StatLabel>
          <StatNumber fontSize="xl" color={totals.totalPdfOnly > 0 ? 'orange.500' : 'green.500'}>
            {totals.totalPdfOnly}
          </StatNumber>
          <StatHelpText>nicht im System {detailFilter === 'pdf-only' ? '▼' : '▶'}</StatHelpText>
        </Stat>
        <Stat
          p="4"
          border={detailFilter === 'system-only' ? '2px solid' : '1px solid'}
          borderColor={detailFilter === 'system-only' ? 'red.400' : cardBorderColor}
          borderRadius="md"
          cursor="pointer"
          onClick={() => handleKpiClick('system-only')}
          _hover={{ borderColor: 'red.300' }}
          transition="all 0.15s"
        >
          <StatLabel>Nur im System</StatLabel>
          <StatNumber fontSize="xl" color={totals.totalSystemOnly > 0 ? 'red.500' : 'green.500'}>
            {totals.totalSystemOnly}
          </StatNumber>
          <StatHelpText>nicht in PDF {detailFilter === 'system-only' ? '▼' : '▶'}</StatHelpText>
        </Stat>
        <Stat
          p="4"
          border={detailFilter === 'code-only' ? '2px solid' : '1px solid'}
          borderColor={detailFilter === 'code-only' ? 'yellow.400' : cardBorderColor}
          borderRadius="md"
          cursor="pointer"
          onClick={() => handleKpiClick('code-only')}
          _hover={{ borderColor: 'yellow.300' }}
          transition="all 0.15s"
        >
          <StatLabel>Ohne Vergütung</StatLabel>
          <StatNumber fontSize="xl" color={codeOnlyEntries.length > 0 ? 'yellow.500' : 'green.500'}>
            {codeOnlyEntries.length}
          </StatNumber>
          <StatHelpText>nur mit Code {detailFilter === 'code-only' ? '▼' : '▶'}</StatHelpText>
        </Stat>
      </Grid>

      {/* Transaction breakdown */}
      <Text fontWeight="bold" mb="3">Transaktionsübersicht (PDF)</Text>
      <Grid templateColumns={['1fr', '1fr 1fr 1fr']} gap="4" mb="6">
        <Box p="4" border="1px solid" borderColor={blueBorder} borderRadius="md" bg={blueBg}>
          <Text fontWeight="semibold" color={blueText}>Abholung (ABHOL.)</Text>
          <Text fontSize="xl" fontWeight="bold">{transactionTotals.pickupCount} Pakete</Text>
          <Text color={amountColor}>{formatCurrency(transactionTotals.pickupAmount)}</Text>
        </Box>
        <Box p="4" border="1px solid" borderColor={purpleBorder} borderRadius="md" bg={purpleBg}>
          <Text fontWeight="semibold" color={purpleText}>Rückgabe (Rückg.)</Text>
          <Text fontSize="xl" fontWeight="bold">{transactionTotals.returnToDriverCount} Pakete</Text>
          <Text color={amountColor}>{formatCurrency(transactionTotals.returnToDriverAmount)}</Text>
        </Box>
        <Box p="4" border="1px solid" borderColor={tealBorder} borderRadius="md" bg={tealBg}>
          <Text fontWeight="semibold" color={tealText}>Annahme (Annah.)</Text>
          <Text fontSize="xl" fontWeight="bold">{transactionTotals.acceptanceCount} Pakete</Text>
          <Text color={amountColor}>{formatCurrency(transactionTotals.acceptanceAmount)}</Text>
        </Box>
      </Grid>

      {/* Detail table for Nur in PDF / Nur im System */}
      {detailFilter && detailFilter !== 'code-only' && detailEntries.length > 0 && (
        <Box mb="6" p="4" bg={detailTableBg} borderRadius="md" border="1px solid" borderColor={cardBorderColor}>
          <Flex justifyContent="space-between" alignItems="center" mb="3">
            <Text fontWeight="bold">
              {detailFilter === 'pdf-only' ? 'Nur in PDF' : 'Nur im System'} — {detailEntries.length} Pakete
            </Text>
            <Button size="sm" leftIcon={<Copy />} variant="outline" onClick={handleCopy}>
              Liste kopieren
            </Button>
          </Flex>
          <Box overflowX="auto" maxH="400px" overflowY="auto">
            <Table size="sm" variant="simple">
              <Thead position="sticky" top="0" bg={detailTableBg} zIndex="1">
                <Tr>
                  <Th>AccessPoint</Th>
                  <Th>Tracking-Nr.</Th>
                  <Th>Status im System</Th>
                </Tr>
              </Thead>
              <Tbody>
                {detailEntries.map((entry, i) => (
                  <Tr key={`${entry.trackingNumber}-${i}`}>
                    <Td>
                      <Text fontSize="xs" fontWeight="semibold">{entry.accessPointID}</Text>
                      <Text fontSize="2xs" color="gray.500">{entry.storeName}</Text>
                    </Td>
                    <Td fontFamily="mono" fontSize="xs">{entry.trackingNumber}</Td>
                    <Td>
                      {entry.systemStatus !== '-' ? (
                        <Badge colorScheme="blue" fontSize="xs">{entry.systemStatus}</Badge>
                      ) : (
                        <Text color="gray.400" fontSize="xs">—</Text>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Box>
      )}
      {detailFilter && detailFilter !== 'code-only' && detailEntries.length === 0 && (
        <Box mb="6" p="4" bg={detailTableBg} borderRadius="md" border="1px solid" borderColor={cardBorderColor}>
          <Text color="gray.500" textAlign="center">Keine Einträge für diesen Filter.</Text>
        </Box>
      )}

      {/* Detail table for Ohne Vergütung */}
      {detailFilter === 'code-only' && codeOnlyEntries.length > 0 && (
        <Box mb="6" p="4" bg={detailTableBg} borderRadius="md" border="1px solid" borderColor={cardBorderColor}>
          <Flex justifyContent="space-between" alignItems="center" mb="3">
            <Text fontWeight="bold">
              Ohne Vergütung — {codeOnlyEntries.length} Sendungen
            </Text>
            <Button size="sm" leftIcon={<Copy />} variant="outline" onClick={handleCopy}>
              Liste kopieren
            </Button>
          </Flex>
          <Box overflowX="auto" maxH="400px" overflowY="auto">
            <Table size="sm" variant="simple">
              <Thead position="sticky" top="0" bg={detailTableBg} zIndex="1">
                <Tr>
                  <Th>AccessPoint</Th>
                  <Th>Tracking-Nr.</Th>
                  <Th>Code</Th>
                  <Th>Status im System</Th>
                </Tr>
              </Thead>
              <Tbody>
                {codeOnlyEntries.map((entry, i) => (
                  <Tr key={`${entry.trackingNumber}-${i}`}>
                    <Td>
                      <Text fontSize="xs" fontWeight="semibold">{entry.accessPointID}</Text>
                      <Text fontSize="2xs" color="gray.500">{entry.storeName}</Text>
                    </Td>
                    <Td fontFamily="mono" fontSize="xs">{entry.trackingNumber}</Td>
                    <Td>
                      <Badge colorScheme="yellow" fontSize="xs">{entry.code}</Badge>
                    </Td>
                    <Td>
                      {entry.systemStatus !== '-' ? (
                        <Badge colorScheme="blue" fontSize="xs">{entry.systemStatus}</Badge>
                      ) : (
                        <Text color="gray.400" fontSize="xs">—</Text>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Box>
      )}
      {detailFilter === 'code-only' && codeOnlyEntries.length === 0 && (
        <Box mb="6" p="4" bg={detailTableBg} borderRadius="md" border="1px solid" borderColor={cardBorderColor}>
          <Text color="gray.500" textAlign="center">Keine Sendungen ohne Vergütung gefunden.</Text>
        </Box>
      )}
    </Box>
  );
};

// ============================================================================
// AccessPoints section with expandable details
// ============================================================================

/** Count code-only tracking numbers in an AP (across all transactions, unfiltered) */
function countCodeOnlyGroups(ap: AccessPointReconciliation): number {
  const groups = groupTransactions(ap.transactions);
  return groups.filter((g) => g.isCodeOnly).length;
}

/** Get tracking numbers that belong to code-only groups */
function getCodeOnlyTrackingNumbers(transactions: TransactionComparison[]): Set<string> {
  const groups = groupTransactions(transactions);
  const set = new Set<string>();
  for (const g of groups) {
    if (g.isCodeOnly) set.add(g.trackingNumber);
  }
  return set;
}

const AccessPointsSection = ({
  accessPoints,
  filter,
  metadata,
}: {
  accessPoints: AccessPointReconciliation[];
  filter: 'all' | 'matched' | 'pdf-only' | 'system-only' | 'code-only';
  metadata: PDFInvoiceMetadata;
}) => {
  const groupBg = useColorModeValue('gray.50', 'gray.700');
  const codeOnlyBg = useColorModeValue('yellow.50', 'yellow.900');
  const separatorColor = useColorModeValue('gray.200', 'gray.600');
  const accentBorderColor = useColorModeValue('gray.200', 'gray.600');
  const toast = useCustomToast();
  const [generateAuszahlungsbeleg, { loading: generating }] = useMutation(GENERATE_AUSZAHLUNGSBELEG);

  const handleGenerate = useCallback(async (ap: AccessPointReconciliation, isTest: boolean) => {
    // Collect all compensated line items (amount > 0) — include coded entries too,
    // as the same parcel can have multiple compensations (e.g. code + uncoded).
    const trackingNumbers = ap.transactions
      .filter((tx) => tx.inPdf && tx.pdfAmount && tx.pdfAmount > 0)
      .map((tx) => tx.trackingNumber);

    if (trackingNumbers.length === 0) {
      toast({ title: 'Fehler', description: 'Keine vergüteten Sendungen für diesen AccessPoint.', status: 'warning' });
      return;
    }

    try {
      const { data } = await generateAuszahlungsbeleg({
        variables: {
          accessPointID: ap.accessPointID,
          storeName: ap.storeName,
          trackingNumbers,
          reportingPeriodStart: metadata.reportingPeriodStart,
          reportingPeriodEnd: metadata.reportingPeriodEnd,
          isTest,
        },
      });

      const url = data?.generateAuszahlungsbeleg;
      if (url) {
        window.open(url, '_blank');
        toast({
          title: isTest ? 'Vorschau erstellt' : 'Auszahlungsbeleg erstellt',
          description: isTest ? 'Testzähler wurde nicht erhöht.' : 'Beleg wurde generiert und Zähler erhöht.',
          status: 'success',
        });
      }
    } catch (error: any) {
      console.error(error);
      toast({
        title: 'Fehler',
        description: error?.message || 'Auszahlungsbeleg konnte nicht erstellt werden.',
        status: 'error',
      });
    }
  }, [generateAuszahlungsbeleg, metadata, toast]);

  // Filter APs: hide cards with no matching transactions for the active filter
  const visibleAPs = useMemo(() => {
    if (filter === 'all') return accessPoints;
    if (filter === 'code-only') return accessPoints.filter((ap) => countCodeOnlyGroups(ap) > 0);
    return accessPoints.filter((ap) =>
      ap.transactions.some((tx) => {
        if (filter === 'matched') return tx.inPdf && tx.inSystem;
        if (filter === 'pdf-only') return tx.inPdf && !tx.inSystem;
        if (filter === 'system-only') return !tx.inPdf && tx.inSystem;
        return true;
      }),
    );
  }, [accessPoints, filter]);

  return (
    <>
      {visibleAPs.length === 0 && (
        <Box py="8" textAlign="center">
          <Text color="gray.500">Keine AccessPoints mit passenden Transaktionen für diesen Filter.</Text>
        </Box>
      )}
      <Accordion allowMultiple>
        {visibleAPs.map((ap) => {
          const codeOnlyCount = countCodeOnlyGroups(ap);
          const codeOnlyTNs = filter === 'code-only' ? getCodeOnlyTrackingNumbers(ap.transactions) : null;

          const filteredTx = ap.transactions.filter((tx) => {
            if (filter === 'matched') return tx.inPdf && tx.inSystem;
            if (filter === 'pdf-only') return tx.inPdf && !tx.inSystem;
            if (filter === 'system-only') return !tx.inPdf && tx.inSystem;
            if (filter === 'code-only') return codeOnlyTNs!.has(tx.trackingNumber);
            return true;
          });

          const groups = groupTransactions(filteredTx);

          return (
            <AccordionItem key={ap.accessPointID} border="1px solid" borderColor={accentBorderColor} borderRadius="md" mb="2">
              <AccordionButton py="3">
                <Grid templateColumns="1fr auto" width="100%" alignItems="center">
                  <Flex alignItems="center" gap="3">
                    <Box textAlign="left">
                      <Text fontWeight="bold" fontSize="sm">{ap.storeName}</Text>
                      <Text fontSize="xs" color="gray.500">{ap.accessPointID}</Text>
                    </Box>
                    {!ap.existsInSystem && (
                      <Badge colorScheme="red" fontSize="xs">Nicht im System</Badge>
                    )}
                  </Flex>
                  <Flex alignItems="center" gap="4">
                    <Flex gap="2" fontSize="xs">
                      <Tag size="sm" colorScheme="green">{ap.matchedCount} Matches</Tag>
                      {ap.pdfOnlyCount > 0 && <Tag size="sm" colorScheme="orange">{ap.pdfOnlyCount} PDF</Tag>}
                      {ap.systemOnlyCount > 0 && <Tag size="sm" colorScheme="red">{ap.systemOnlyCount} System</Tag>}
                      {codeOnlyCount > 0 && <Tag size="sm" colorScheme="yellow">{codeOnlyCount} Ohne Vergütung</Tag>}
                    </Flex>
                    <Text fontWeight="bold" fontSize="sm" color="green.600">{formatCurrency(ap.pdfCompensation)}</Text>
                    <AccordionIcon />
                  </Flex>
                </Grid>
              </AccordionButton>
              <AccordionPanel pb="4">
                <Flex justifyContent="space-between" alignItems="flex-start" mb="4" flexWrap="wrap" gap="3">
                  <Grid templateColumns={['1fr 1fr', '1fr 1fr 1fr 1fr']} gap="3" flex="1">
                    <Box>
                      <Text fontSize="xs" color="gray.500">PDF Transaktionen</Text>
                      <Text fontWeight="bold">{ap.pdfTransactionCount}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">Davon vergütet</Text>
                      <Text fontWeight="bold">{ap.pdfCompensatedCount}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">System Pakete</Text>
                      <Text fontWeight="bold">{ap.systemParcelCount}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">Vergütung</Text>
                      <Text fontWeight="bold" color="green.600">{formatCurrency(ap.pdfCompensation)}</Text>
                    </Box>
                  </Grid>
                  <ButtonGroup size="sm" variant="outline">
                    <Button
                      leftIcon={<Icon as={Eye} />}
                      isLoading={generating}
                      onClick={() => handleGenerate(ap, true)}
                    >
                      Vorschau
                    </Button>
                    <Button
                      leftIcon={<Icon as={FilePdf} />}
                      colorScheme="brand"
                      isLoading={generating}
                      onClick={() => handleGenerate(ap, false)}
                    >
                      Auszahlungsbeleg
                    </Button>
                  </ButtonGroup>
                </Flex>

                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Status</Th>
                        <Th>Tracking-Nr.</Th>
                        <Th>Service</Th>
                        <Th>Datum</Th>
                        <Th isNumeric>Betrag</Th>
                        <Th>Code</Th>
                        <Th>System-Status</Th>
                        <Th>Pickup</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {groups.map((group, gi) => (
                        <React.Fragment key={group.trackingNumber}>
                          {group.entries.map((tx, ti) => {
                            const isMulti = group.entries.length > 1;
                            const isLastInGroup = ti === group.entries.length - 1;
                            const rowBg = group.isCodeOnly ? codeOnlyBg : isMulti ? groupBg : undefined;

                            return (
                              <Tr
                                key={`${tx.trackingNumber}-${ti}`}
                                bg={rowBg}
                                opacity={tx.pdfCode && !(tx.pdfAmount && tx.pdfAmount > 0) ? 0.75 : 1}
                                borderBottom={isLastInGroup && gi < groups.length - 1 && isMulti ? '2px solid' : undefined}
                                borderBottomColor={separatorColor}
                              >
                                <Td><StatusBadge inPdf={tx.inPdf} inSystem={tx.inSystem} /></Td>
                                <Td fontFamily="mono" fontSize="xs">
                                  {ti === 0 ? (
                                    <Flex alignItems="center" gap="1" flexWrap="wrap">
                                      <Text as="span">{tx.trackingNumber}</Text>
                                      {isMulti && (
                                        <Badge colorScheme="gray" fontSize="2xs">{group.entries.length}x</Badge>
                                      )}
                                      {group.isCodeOnly && (
                                        <Tooltip label="Nur kodierte Einträge vorhanden — keine Vergütung" placement="top">
                                          <Badge colorScheme="yellow" fontSize="2xs" cursor="help">Ohne Vergütung</Badge>
                                        </Tooltip>
                                      )}
                                    </Flex>
                                  ) : (
                                    <Text color="gray.400">↳</Text>
                                  )}
                                </Td>
                                <Td>{tx.pdfService ? serviceLabel(tx.pdfService) : '-'}</Td>
                                <Td>{tx.pdfDate ? formatDate(tx.pdfDate) : '-'}</Td>
                                <Td isNumeric>{tx.pdfAmount !== undefined ? formatCurrency(tx.pdfAmount) : '-'}</Td>
                                <Td>
                                  {tx.pdfCode ? (
                                    <Tooltip label={tx.pdfCodeDescription || tx.pdfCode} placement="top">
                                      <Badge colorScheme="yellow" cursor="help">{tx.pdfCode}</Badge>
                                    </Tooltip>
                                  ) : '-'}
                                </Td>
                                <Td>
                                  {tx.systemStatus ? (
                                    <Badge colorScheme="blue" fontSize="xs">{tx.systemStatus}</Badge>
                                  ) : '-'}
                                </Td>
                                <Td fontSize="xs">
                                  {tx.systemEvents?.consigneePickupAt
                                    ? formatDateTime(tx.systemEvents.consigneePickupAt)
                                    : tx.systemEvents?.dispossessedAt
                                      ? formatDateTime(tx.systemEvents.dispossessedAt)
                                      : '-'}
                                </Td>
                              </Tr>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </AccordionPanel>
            </AccordionItem>
          );
        })}
      </Accordion>
    </>
  );
};

// ============================================================================
// Summary table section (from PDF "Zusammenfassung Child Access Points")
// ============================================================================

const SummaryTableSection = ({ summary }: { summary: PDFAccessPointSummary[] }) => {
  const totalRowBg = useColorModeValue('gray.100', 'gray.700');

  return (
    <Box>
      <Text fontWeight="bold" mb="3">
        Zusammenfassung Child Access Points (aus PDF)
      </Text>
      <Box overflowX="auto">
        <Table size="sm" variant="striped">
          <Thead>
            <Tr>
              <Th>Store</Th>
              <Th>AP ID</Th>
              <Th isNumeric>Vergütung</Th>
              <Th isNumeric>MwSt</Th>
              <Th isNumeric>Gesamt</Th>
            </Tr>
          </Thead>
          <Tbody>
            {summary.map((row) => (
              <Tr key={row.accessPointID}>
                <Td>{row.storeName}</Td>
                <Td fontFamily="mono" fontSize="xs">{row.accessPointID}</Td>
                <Td isNumeric>{formatCurrency(row.compensationAmount)}</Td>
                <Td isNumeric>{formatCurrency(row.tax)}</Td>
                <Td isNumeric fontWeight="semibold">{formatCurrency(row.total)}</Td>
              </Tr>
            ))}
          </Tbody>
          <Tbody>
            <Tr fontWeight="bold" bg={totalRowBg}>
              <Td colSpan={2}>Gesamt</Td>
              <Td isNumeric>
                {formatCurrency(summary.reduce((sum, r) => sum + r.compensationAmount, 0))}
              </Td>
              <Td isNumeric>
                {formatCurrency(summary.reduce((sum, r) => sum + r.tax, 0))}
              </Td>
              <Td isNumeric>
                {formatCurrency(summary.reduce((sum, r) => sum + r.total, 0))}
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default ReconciliationPage;
